import { useMemo } from 'react'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import moment from 'moment'
import type { WeeklyBucket } from '@/utils/analytics'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const LOW_RISK_WEEKLY = 10
const HIGH_RISK_WEEKLY = 20

// Inline plugin to draw NHMRC guideline lines across the chart area
const guidelinePlugin = {
  id: 'nhmrcGuidelines',
  afterDatasetsDraw(chart: { ctx: CanvasRenderingContext2D; chartArea: { left: number; right: number; top: number; bottom: number }; scales: Record<string, { getPixelForValue: (v: number) => number; max: number }> }) {
    const { ctx, chartArea, scales } = chart
    const y = scales.y
    if (!y || !chartArea) return
    const drawLine = (value: number, color: string, label: string) => {
      if (value > y.max && y.max > 0) return
      const px = y.getPixelForValue(value)
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = 1.5
      ctx.setLineDash([6, 4])
      ctx.beginPath()
      ctx.moveTo(chartArea.left, px)
      ctx.lineTo(chartArea.right, px)
      ctx.stroke()
      ctx.setLineDash([])
      ctx.fillStyle = color
      ctx.font = '10px system-ui, sans-serif'
      ctx.textBaseline = 'bottom'
      ctx.fillText(label, chartArea.left + 4, px - 2)
      ctx.restore()
    }
    drawLine(LOW_RISK_WEEKLY, '#f59e0b', `NHMRC low-risk: ${LOW_RISK_WEEKLY}/wk`)
    drawLine(HIGH_RISK_WEEKLY, '#ef4444', `High-risk: ${HIGH_RISK_WEEKLY}/wk`)
  },
}

interface WeeklyStandardDrinksChartProps {
  weeks: WeeklyBucket[]
}

export default function WeeklyStandardDrinksChart({ weeks }: WeeklyStandardDrinksChartProps) {
  const chartData = useMemo(() => ({
    labels: weeks.map(w => moment(w.weekStart).format('D MMM')),
    datasets: [
      {
        label: 'Standard drinks',
        data: weeks.map(w => Math.round(w.stdDrinks * 10) / 10),
        backgroundColor: weeks.map(w =>
          w.stdDrinks >= HIGH_RISK_WEEKLY ? '#ef4444'
            : w.stdDrinks >= LOW_RISK_WEEKLY ? '#f59e0b'
            : '#2dd4bf'
        ),
        borderRadius: 3,
      },
    ],
  }), [weeks])

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgb(30, 41, 59)',
        titleColor: 'rgb(248, 250, 252)',
        bodyColor: 'rgb(148, 163, 184)',
        borderColor: 'rgb(71, 85, 105)',
        borderWidth: 1,
        callbacks: {
          title: (items: { dataIndex: number }[]) => {
            const w = weeks[items[0]?.dataIndex]
            if (!w) return ''
            return `Week of ${moment(w.weekStart).format('ddd D MMM YYYY')}`
          },
          label: (item: { dataIndex: number; parsed: { y: number } }) => {
            const w = weeks[item.dataIndex]
            if (!w) return ''
            return [
              `${item.parsed.y.toFixed(1)} standard drinks`,
              `${w.drinks} drink${w.drinks === 1 ? '' : 's'} total`,
            ]
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { color: 'rgb(148, 163, 184)', autoSkip: true, maxTicksLimit: 12 },
        grid: { color: 'rgba(71, 85, 105, 0.2)' },
      },
      y: {
        beginAtZero: true,
        ticks: { color: 'rgb(148, 163, 184)' },
        grid: { color: 'rgba(71, 85, 105, 0.3)' },
      },
    },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }), [weeks]) as any

  if (weeks.length === 0) {
    return (
      <div className="card">
        <h2 className="text-sm font-semibold text-[var(--text-primary)] mb-2">Weekly standard drinks</h2>
        <p className="text-sm text-[var(--text-secondary)]">No data in this period.</p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-sm font-semibold text-[var(--text-primary)]">Weekly standard drinks</h2>
        <p className="text-xs text-[var(--text-secondary)]">vs NHMRC guidelines</p>
      </div>
      <div className="h-56">
        <Bar data={chartData} options={options} plugins={[guidelinePlugin]} />
      </div>
    </div>
  )
}
